import {
  TRANSFORMATION_STATUS_LABELS,
  type StockTransformation,
} from "@artisancode/api-types";
import { CheckCircle2, Eye, MoreHorizontal, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateWIB } from "@/lib/date-utils";

import { summarizeRepacking } from "./repacking-utils";

export interface RepackingRowsProps {
  rows: StockTransformation[];
  canComplete: boolean;
  isCompleting?: boolean;
  onViewDetail: (trans: StockTransformation) => void;
  onComplete: (id: string) => void;
  onCancelRequest: (trans: StockTransformation) => void;
}

const HEAD = "h-10 text-xs font-medium";

export function RepackingDesktopTable({
  rows,
  canComplete,
  isCompleting,
  onViewDetail,
  onComplete,
  onCancelRequest,
}: RepackingRowsProps) {
  return (
    <div className="relative w-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className={HEAD}>Kode</TableHead>
            <TableHead className={HEAD}>Tanggal</TableHead>
            <TableHead className={HEAD}>Bahan Baku Asal (Input)</TableHead>
            <TableHead className={`${HEAD} text-right`}>
              Dibuka (Input)
            </TableHead>
            <TableHead className={`${HEAD} text-right`}>
              Ke Wadah (Output)
            </TableHead>
            <TableHead className={`${HEAD} text-right`}>Susut Proses</TableHead>
            <TableHead className={`${HEAD} text-center`}>Status</TableHead>
            <TableHead className={`${HEAD} w-24 pr-4 text-right`}>
              Aksi
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                className="h-32 text-center text-xs text-muted-foreground"
              >
                Tidak ada dokumen repacking yang sesuai kriteria.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((item) => {
              const statusMeta = TRANSFORMATION_STATUS_LABELS[item.status];
              const {
                inputLine,
                outputLine,
                inputUom,
                outputUom,
                shrinkagePercent,
              } = summarizeRepacking(item);

              return (
                <TableRow key={item.id} className="hover:bg-muted/20">
                  <TableCell className="py-2.5 font-mono text-xs font-medium">
                    {item.code}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-2.5 text-xs text-muted-foreground">
                    {formatDateWIB(item.date)}
                  </TableCell>
                  <TableCell className="py-2.5">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-foreground">
                        {inputLine?.itemName || "-"}
                      </span>
                      {item.note && (
                        <span className="line-clamp-1 text-[11px] text-muted-foreground">
                          {item.note}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs font-medium">
                    {item.totalInputQty.toLocaleString("id-ID")}{" "}
                    <span className="text-[10px] text-muted-foreground">
                      {inputUom}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    {item.totalOutputQty.toLocaleString("id-ID")}{" "}
                    <span className="text-[10px] text-muted-foreground">
                      {outputUom}
                      {outputLine && outputLine.itemId !== inputLine?.itemId
                        ? ` ${outputLine.itemName}`
                        : ""}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs">
                    {item.shrinkageQty > 0 ? (
                      <span className="font-medium text-rose-600 dark:text-rose-400">
                        {item.shrinkageQty.toLocaleString("id-ID")}{" "}
                        <span className="text-[10px] text-muted-foreground">
                          {outputUom} ({shrinkagePercent}%)
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-center">
                    <Badge
                      variant={statusMeta.variant}
                      className="text-[10px] font-normal capitalize"
                    >
                      {statusMeta.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5 pr-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="text-xs">
                        <DropdownMenuItem
                          onClick={() => onViewDetail(item)}
                          className="cursor-pointer gap-2"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Lihat Rincian
                        </DropdownMenuItem>

                        {item.status === "draft" && canComplete && (
                          <DropdownMenuItem
                            onClick={() => onComplete(item.id)}
                            disabled={isCompleting}
                            className="cursor-pointer gap-2 font-medium text-emerald-600 dark:text-emerald-400"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Selesaikan Repacking
                          </DropdownMenuItem>
                        )}

                        {/* A completed repack cannot be undone: opened material never goes back to on-hand */}
                        {item.status === "draft" && (
                          <DropdownMenuItem
                            onClick={() => onCancelRequest(item)}
                            className="cursor-pointer gap-2 text-destructive focus:text-destructive"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            Batalkan Draft
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
